import React, { useState } from "react";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  EnvironmentOutlined,
  HeartFilled,
  HeartOutlined,
  StarFilled,
} from "@ant-design/icons";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import {
  formatFx,
  formatIls,
  getPackageCurrency,
} from "../unifiedSearchMockup/demoFixtures";
import { formatHotelStayHeader } from "../unifiedSearchMockup/formatHotelStayHeader";
import { formatHubwayzSaleDisplay } from "../unifiedSearchMockup/hubwayz/hubwayzRoomSaleDisplay";

function HotelMedia({ imageUrl, hotelName }) {
  const [failed, setFailed] = useState(false);
  const src = String(imageUrl || "").trim();
  const showPhoto = Boolean(src) && !failed;

  if (!showPhoto) {
    return (
      <div
        className="hrm-card__media-placeholder"
        role="img"
        aria-label={hotelName || "HotelsRiver"}
      >
        <img src={HOTELSRIVER_OFFICIAL_LOGO_URL} alt="HotelsRiver" />
        <span>HotelsRiver</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={hotelName || ""}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function StarRating({ stars }) {
  const count = Math.max(0, Math.min(5, Number(stars) || 0));
  if (!count) return null;
  return (
    <span className="hrm-stars" aria-label={`${count} כוכבים`}>
      {Array.from({ length: count }).map((_, i) => (
        <StarFilled key={i} />
      ))}
    </span>
  );
}

function AllocationBadge({ immediate, variant = "chip" }) {
  const label = immediate ? "אישור מיידי" : "ממתין לאישור";
  const Icon = immediate ? CheckCircleFilled : ClockCircleOutlined;
  return (
    <span
      className={`hrm-alloc hrm-alloc--${immediate ? "immediate" : "pending"} hrm-alloc--${variant}`}
    >
      <Icon aria-hidden />
      {label}
    </span>
  );
}

/** Hotels-only SERP card — stay total from Hubwayz, no flight strip. */
export default function HotelResultCard({
  hotel,
  viewMode = "list",
  compared = false,
  favorited = false,
  onToggleCompare,
  onToggleFavorite,
  onSelect,
}) {
  const currency = getPackageCurrency(hotel) || hotel.currency || "USD";
  const pax = Math.max(1, Number(hotel.paxCount) || 2);
  const salePriced = hotel.saleOk === true && Number.isFinite(Number(hotel.saleDisplayAmount));
  const saleBlocked = hotel.saleOk === false;
  const stayTotal = Number(hotel.hotelNetTotal);
  const perPerson = Number(hotel.agentNetPerPerson ?? hotel.pricePerPerson);
  const totalFx = salePriced
    ? Number(hotel.saleDisplayAmount)
    : Number.isFinite(stayTotal)
      ? stayTotal
      : Number.isFinite(perPerson)
        ? perPerson * pax
        : null;
  const displayCurrency = salePriced ? hotel.saleDisplayCurrency : currency;
  const perFx = salePriced
    ? Number(hotel.saleDisplayAmount) / pax
    : Number.isFinite(perPerson)
      ? perPerson
      : totalFx != null
        ? totalFx / pax
        : null;
  const totalLabel = salePriced
    ? formatHubwayzSaleDisplay(totalFx, displayCurrency)
    : saleBlocked
      ? "—"
      : totalFx != null
        ? formatFx(totalFx, currency)
        : "—";
  const perLabel =
    salePriced && perFx != null
      ? formatHubwayzSaleDisplay(perFx, displayCurrency)
      : !saleBlocked && perFx != null
        ? formatFx(perFx, currency)
        : null;
  const stay = formatHotelStayHeader({
    dateRangeLabel: hotel.dateRangeLabel,
    board: hotel.boardLabel,
  });

  return (
    <article
      className={`hrm-card${viewMode === "grid" ? " hrm-card--grid" : ""}`}
      data-hotel-id={hotel.id}
    >
      <div className="hrm-card__media">
        <HotelMedia imageUrl={hotel.imageUrl} hotelName={hotel.hotelName} />
        <div className="hrm-card__media-badges">
          <AllocationBadge immediate={!!hotel.immediateConfirm} variant="overlay" />
        </div>
      </div>

      <div className="hrm-card__body">
        <div className="hrm-card__title-row">
          <h3 className="hrm-card__hotel">{hotel.hotelName}</h3>
          <StarRating stars={hotel.stars} />
        </div>
        <p className="hrm-card__location">
          <EnvironmentOutlined aria-hidden />
          {[hotel.city, hotel.country].filter(Boolean).join(", ")}
        </p>
        <div className="hrm-card__tags">
          <span className="hrm-tag hsm-tag--hotel">מלון</span>
          {stay.boardLabel ? <span className="hrm-tag">{stay.boardLabel}</span> : null}
          {hotel.roomType ? <span className="hrm-tag">{hotel.roomType}</span> : null}
        </div>
        <p className="hrm-card__dates">
          {stay.dates ? <bdi dir="ltr">{stay.dates}</bdi> : hotel.dateRangeLabel}
          {hotel.nights ? ` • ${hotel.nights} לילות` : ""}
        </p>
      </div>

      <div className="hrm-card__action">
        <div className="hrm-card__price-block">
          <div className="hrm-card__price">
            <span className="hrm-card__price-fx">{totalLabel}</span>
            {!salePriced && !saleBlocked && Number.isFinite(Number(hotel.agentNetPerPersonIls)) ? (
              <span className="hrm-card__price-ils">
                {formatIls(hotel.agentNetPerPersonIls * pax)}
              </span>
            ) : null}
          </div>
          <div className="hrm-card__price-meta">
            {salePriced ? "סה״כ נטו לסוכן" : "סה״כ לשהייה"}
          </div>
          {perLabel ? (
            <div className="hrm-card__total">
              לאדם {perLabel}
              <span className="hrm-card__total-sep" aria-hidden>
                ·
              </span>
              ל־{pax} אורחים
            </div>
          ) : null}
        </div>
        <button type="button" className="hrm-btn-primary" onClick={() => onSelect?.(hotel)}>
          בחר מלון
        </button>
        <div className="hrm-card__tools">
          <label className="hrm-compare">
            <input
              type="checkbox"
              checked={compared}
              onChange={() => onToggleCompare?.(hotel.id)}
            />
            השוואה
          </label>
          <button
            type="button"
            className={`hrm-fav${favorited ? " hrm-fav--on" : ""}`}
            aria-label={favorited ? "הסר ממועדפים" : "הוסף למועדפים"}
            onClick={() => onToggleFavorite?.(hotel.id)}
          >
            {favorited ? <HeartFilled /> : <HeartOutlined />}
          </button>
        </div>
      </div>
    </article>
  );
}
